import type { Breadcrumb, ErrorReport, ErrorReportingUser, Reporter, ScopeTags } from "./types";

/** A call made before init, replayed in order once the reporter is ready. */
type QueuedCall =
  | { readonly kind: "report"; readonly report: ErrorReport }
  | { readonly kind: "breadcrumb"; readonly breadcrumb: Breadcrumb }
  | { readonly kind: "identify"; readonly user: ErrorReportingUser }
  | { readonly kind: "tags"; readonly tags: ScopeTags };

type Status =
  | { readonly kind: "queueing"; readonly queue: QueuedCall[] }
  | { readonly kind: "ready"; readonly reporter: Reporter }
  /** Init threw: everything is dropped from then on. */
  | { readonly kind: "failed" };

interface ErrorReportingState {
  status: Status;
}

function createState(): ErrorReportingState {
  return { status: { kind: "queueing", queue: [] } };
}

// Module-level singleton: errors are reported from non-React code (API client,
// stores) and the SDK behind the reporter is itself global.
let state = createState();

export function replaceState(): void {
  state = createState();
}

export function start(reporter: Reporter): void {
  const { status } = state;
  if (status.kind !== "queueing") {
    return;
  }
  try {
    reporter.init();
  } catch (error) {
    console.error("[a3s-error-reporting] Init failed; dropped queued errors:", error);
    state.status = { kind: "failed" };
    return;
  }
  for (const call of status.queue) {
    deliver(reporter, call);
  }
  state.status = { kind: "ready", reporter };
}

export function send(report: ErrorReport): void {
  dispatch({ kind: "report", report });
}

export function leaveBreadcrumb(breadcrumb: Breadcrumb): void {
  dispatch({ kind: "breadcrumb", breadcrumb });
}

export function setUser(user: ErrorReportingUser): void {
  dispatch({ kind: "identify", user });
}

export function mergeTags(tags: ScopeTags): void {
  dispatch({ kind: "tags", tags });
}

function dispatch(call: QueuedCall): void {
  const { status } = state;
  switch (status.kind) {
    case "queueing":
      enqueue(status.queue, call);
      return;
    case "ready":
      deliver(status.reporter, call);
      return;
    case "failed":
      return;
  }
}

/**
 * Caps memory if init never happens. Identify and tag calls are always kept, so
 * queued errors stay attributed. Errors past the cap are discarded rather than the
 * earliest, since the first error usually caused the rest; breadcrumbs past
 * the cap push out the oldest, as Sentry does, since the latest steps are the
 * ones that led to the error.
 */
const MAX_QUEUED_REPORTS = 50;
const MAX_QUEUED_BREADCRUMBS = 100;

function enqueue(queue: QueuedCall[], call: QueuedCall): void {
  const sameKind = queue.filter((queued) => queued.kind === call.kind).length;
  if (call.kind === "report" && sameKind >= MAX_QUEUED_REPORTS) {
    return;
  }
  if (call.kind === "breadcrumb" && sameKind >= MAX_QUEUED_BREADCRUMBS) {
    queue.splice(
      queue.findIndex((queued) => queued.kind === "breadcrumb"),
      1,
    );
  }
  queue.push(call);
}

/** Reporting runs inside error handling, so it must never throw: reporter errors are logged and swallowed. */
function deliver(reporter: Reporter, call: QueuedCall): void {
  try {
    switch (call.kind) {
      case "report":
        reporter.captureException(call.report);
        return;
      case "breadcrumb":
        reporter.addBreadcrumb(call.breadcrumb);
        return;
      case "identify":
        reporter.setUser(call.user);
        return;
      case "tags":
        reporter.setTags(call.tags);
        return;
    }
  } catch (error) {
    console.error(`[a3s-error-reporting] ${call.kind} failed:`, error);
  }
}
